import React, { useState, useEffect } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { XCircle, Wallet, AlertCircle, CreditCard, CheckCircle, Sparkles, ChevronRight } from 'lucide-react';
import { useWallet } from '../context/WalletContext';

const PredictionForm = ({ match, onClose }) => {
  const [user, loading, error] = useAuthState(auth);
  const [predictionType, setPredictionType] = useState('');
  const [winnerTeam, setWinnerTeam] = useState(match.team1);
  const [firstInningScore, setFirstInningScore] = useState('');
  const [overNumber, setOverNumber] = useState('');
  const [overRuns, setOverRuns] = useState('2'); // Default to the first option (0-4)
  const [overWickets, setOverWickets] = useState('');
  const [selectedInning, setSelectedInning] = useState('1'); // Default to 1st innings
  const [betAmount, setBetAmount] = useState('10');
  
  // Use wallet context instead of local state
  const { wallets, spendCoins, loading: walletLoading } = useWallet();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [matchStarted, setMatchStarted] = useState(false);

  // Check auth state
  useEffect(() => {
    if (error) {
      console.error('Auth error:', error);
      setErrorMessage('Authentication error. Please sign in again.');
    }
  }, [error]);

  // Set default values when prediction type changes
  useEffect(() => {
    if (predictionType === 'over_runs' || predictionType === 'over_wickets') {
      setSelectedInning('1'); // Set default to 1st innings
      if (predictionType === 'over_runs' && !overRuns) {
        setOverRuns('2'); // Default to "0-4" range
      }
    }
  }, [predictionType]);
  
  let maxOvers = 20;
  // Check match timing
  useEffect(() => {
    const checkMatchTiming = () => {
      if (!match) return;
      
      // Check if match has started
      const now = new Date();
      const matchStartTime = match.startTime?.toDate ? match.startTime.toDate() : new Date(match.startTime);
      setMatchStarted(now >= matchStartTime);
    };

    checkMatchTiming();
  }, [match]);

  const getAvailableOvers = () => {
    // Determine max overs based on match format
     // Default T20
    
    if (match.matchType === 'odi' || match.format === 'odi') {
      maxOvers = 50;
    } else if (match.matchType === 'test' || match.format === 'test') {
      maxOvers = 90; // Typical day's play
    }
    
    const overs = [];
    for (let i = 1; i <= maxOvers; i++) {
      overs.push(i);
    }
    
    return overs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Ensure user is logged in
      if (!user) {
        setErrorMessage('You must be logged in to make predictions');
        return;
      }

      // Check if match has started
      if (matchStarted) {
        setErrorMessage('Cannot place predictions after match has started');
        return;
      }

      // Validate bet amount
      const betAmountNum = parseFloat(betAmount);
      if (betAmountNum <= 0) {
        setErrorMessage('Bet amount must be greater than 0');
        return;
      }

      if (betAmountNum > wallets.walletA) {
        setErrorMessage('Insufficient balance in Wallet A');
        return;
      }
      
      setIsSubmitting(true);
      
      // Prepare prediction data based on type
      let predictionData = {
        userId: user.uid,
        matchId: match.id,
        type: predictionType,
        betAmount: betAmountNum,
        potentialWinning: betAmountNum * 3, // 3x multiplier
        createdAt: serverTimestamp(),
        status: 'pending', // 'pending', 'correct', 'wrong'
      };
      
      switch (predictionType) {
        case 'winner':
          predictionData.value = winnerTeam;
          break;
        case 'first_inning_score':
          if (!firstInningScore || firstInningScore < 0) {
            setErrorMessage('Please enter a valid score');
            return;
          }
          predictionData.value = parseInt(firstInningScore);
          break;
        case 'over_runs':
          if (!overNumber) {
            setErrorMessage('Please select over number');
            return;
          }
          predictionData.value = parseInt(overRuns);
          predictionData.overNumber = parseInt((overNumber)-1);
          predictionData.inningIndex = parseInt(selectedInning) - 1; // Convert to 0-indexed (1->0, 2->1)
          break;
        case 'over_wickets':
          if (!overNumber || !overWickets || overWickets < 0 || overWickets > 6) {
            setErrorMessage('Please enter valid over number and wickets (0-6)');
            return;
          }
          predictionData.value = parseInt(overWickets);
          predictionData.overNumber = parseInt((overNumber)-1);
          predictionData.inningIndex = parseInt(selectedInning) - 1;
          break;
        default:
          setErrorMessage('Please select a prediction type');
          return;
      }

      // Use the wallet context's spendCoins function to deduct from walletA
      const spendSuccess = await spendCoins(betAmountNum);
      
      if (!spendSuccess) {
        setErrorMessage('Failed to deduct bet amount from wallet. Please try again.');
        return;
      }

      // Add prediction to Firestore
      const predictionsRef = collection(db, 'predictions');
      await addDoc(predictionsRef, predictionData);
      
      // Display success message
      setSuccessMessage(`Prediction submitted successfully! ${betAmountNum} coins deducted from Wallet A`);
      
      // Reset form or close after success
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting prediction:', error);
      setErrorMessage('Failed to submit prediction. Please try again.');
      
      // If there was an error after spending coins, we should ideally refund
      // But since we're using the wallet context which handles Firestore,
      // we might need to add a refund function to the wallet context
      // For now, we'll just show the error and the wallet will be updated on next refresh
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading if wallet is still loading
  if (loading || walletLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl relative max-w-md mx-auto">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading your wallet data...</p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl relative max-w-md mx-auto">
        <button 
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={onClose}
        >
          <XCircle size={24} />
        </button>
        <div className="flex flex-col items-center justify-center py-8">
          <div className="bg-yellow-50 dark:bg-yellow-900/30 p-3 rounded-full mb-4">
            <AlertCircle size={40} className="text-yellow-500" />
          </div>
          <h2 className="text-xl font-bold mb-3 dark:text-white">Sign In Required</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300 text-center">
            You need to be signed in to make predictions and win rewards.
          </p>
          <button
            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex justify-center items-center font-medium"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Show message if match has started
  if (matchStarted) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl relative max-w-md mx-auto">
        <button 
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={onClose}
        >
          <XCircle size={24} />
        </button>
        <div className="flex flex-col items-center justify-center py-8">
          <div className="bg-orange-50 dark:bg-orange-900/30 p-3 rounded-full mb-4">
            <AlertCircle size={40} className="text-orange-500" />
          </div>
          <h2 className="text-xl font-bold mb-3 dark:text-white">Match Started</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300 text-center">
            Predictions are no longer available for this match as it has already started.
          </p>
          <button
            className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 flex justify-center items-center font-medium"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

const predictionOptions = [
  ...(match.status === 'Match not started' ? [{ id: 'winner', label: 'Match Winner', icon: <CheckCircle size={18} /> }] : []),
  { id: 'first_inning_score', label: 'First Innings Score', icon: <Sparkles size={18} /> },
  { id: 'over_runs', label: 'Runs in an Over', icon: <ChevronRight size={18} /> },
  { id: 'over_wickets', label: 'Wickets in an Over', icon: <ChevronRight size={18} /> }
];

  const betOptions = [10, 50, 100, 500];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl relative max-w-md mx-auto max-h-[90vh] overflow-y-auto">
      <button 
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        onClick={onClose}
      >
        <XCircle size={24} />
      </button>
      
      <h2 className="text-xl font-bold mb-1 dark:text-white">Predict & Win</h2>
      <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-6 font-medium">{match.name}</h3>
      
      {/* Wallet Info Card */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl mb-6 overflow-hidden">
        <div className="px-5 py-4 text-white">
          <div className="flex items-center mb-2">
            <CreditCard size={20} className="mr-2" />
            <span className="text-sm font-medium text-blue-100">Your Balance</span>
          </div>
          <div className="text-2xl font-bold">{wallets.walletA || 0} coins</div>
          <div className="text-sm text-blue-200 mt-1">₹{wallets.walletB || 0} available for withdrawal</div>
        </div>
      </div>
      
      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-4 rounded-lg mb-6 text-sm flex items-start">
          <AlertCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-4 rounded-lg mb-6 text-sm flex items-start">
          <CheckCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Prediction Type */}
        <div>
          <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">Prediction Type</label>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1.5 grid grid-cols-2 gap-2">
            {predictionOptions.map(option => {
              // Check if this option should be disabled
              const overss = match.matchType === "t20" ? 20 : (match.matchType === "odi" ? 50 : 0);
              const isFirstInningDisabled = 
    option.id === 'first_inning_score' &&
    match.scorecard?.innings &&
    match.scorecard.innings.length > 1 &&
    ((match.matchType !== "test" && parseInt(match.scorecard.innings[0]?.overs || 0) > maxOvers - 5) ||
     (match.matchType === "test" && parseInt(match.scorecard.innings[0]?.overs || 0) >= maxOvers));
              
              if (isFirstInningDisabled) return null;
              
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setPredictionType(option.id)}
                  className={`flex items-center p-3 rounded-md transition-colors ${
                    predictionType === option.id ? 
                    'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm' : 
                    'text-gray-500 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-600/50'
                  }`}
                >
                  <span className="mr-2">{option.icon}</span>
                  <span className="text-sm font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bet Amount */}
        <div>
          <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">
            Bet Amount <span className="text-blue-600 dark:text-blue-400 font-normal">(Win 3x)</span>
          </label>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {betOptions.map(amount => (
              <button
                key={amount}
                type="button"
                onClick={() => setBetAmount(amount.toString())}
                className={`py-2.5 px-1 rounded-lg text-center transition-colors ${
                  betAmount === amount.toString() ?
                  'bg-blue-600 text-white font-medium' :
                  'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                disabled={(wallets.walletA || 0) < amount}
              >
                {amount}
              </button>
            ))}
          </div>
          <div className="relative">
            <input 
              type="number" 
              min="1"
              max={wallets.walletA || 0}
              step="1"
              className="w-full pl-8 pr-3 py-2.5 bg-gray-50 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              placeholder="Custom amount"
              required
            />
            <Wallet size={16} className="absolute left-3 top-3 text-gray-400" />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center">
            <Sparkles size={14} className="mr-1" />
            Potential winning: ₹{(parseFloat(betAmount) * 3)/100 || 0}
          </p>
        </div>
        
        {/* Prediction Type Specific Forms */}
        {predictionType === 'winner' && (
          <div>
            <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">Winning Team</label>
            <div className="grid grid-cols-2 gap-3">
              <label className={`flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg p-3 cursor-pointer border-2 transition-colors ${
                winnerTeam === match.team1 ? 
                'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 
                'border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}>
                <input 
                  type="radio" 
                  name="winner" 
                  className="sr-only"
                  checked={winnerTeam === match.team1}
                  onChange={() => setWinnerTeam(match.team1)}
                  required
                />
                <div className="w-4 h-4 mr-2 rounded-full border flex-shrink-0 flex items-center justify-center
                  ${winnerTeam === match.team1 ? 
                  'border-blue-600 bg-blue-600 dark:border-blue-400 dark:bg-blue-500' : 
                  'border-gray-400 dark:border-gray-500'
                }">
                  {winnerTeam === match.team1 && 
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  }
                </div>
                <span className="text-sm font-medium truncate dark:text-white">{match.team1}</span>
              </label>
              
              <label className={`flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg p-3 cursor-pointer border-2 transition-colors ${
                winnerTeam === match.team2 ? 
                'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 
                'border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}>
                <input 
                  type="radio" 
                  name="winner"
                  className="sr-only"
                  checked={winnerTeam === match.team2}
                  onChange={() => setWinnerTeam(match.team2)}
                />
                <div className={`w-4 h-4 mr-2 rounded-full border flex-shrink-0 flex items-center justify-center
                  ${winnerTeam === match.team2 ? 
                  'border-blue-600 bg-blue-600 dark:border-blue-400 dark:bg-blue-500' : 
                  'border-gray-400 dark:border-gray-500'
                }`}>
                  {winnerTeam === match.team2 && 
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  }
                </div>
                <span className="text-sm font-medium truncate dark:text-white">{match.team2}</span>
              </label>
            </div>
          </div>
        )}
        
        {predictionType === 'first_inning_score' && (
  <div>
    <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">
      First Innings Score
    </label>
    <select
      className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
      value={firstInningScore}
      onChange={(e) => setFirstInningScore(Number(e.target.value))}
      required
    >
      <option value="">Select a range</option>
      <option value="10">1-20</option>
      <option value="30">21-40</option>
      <option value="50">41-60</option>
      <option value="70">61-80</option>
      <option value="90">81-100</option>
      <option value="110">101-120</option>
      <option value="130">121-140</option>
      <option value="150">141-160</option>
      <option value="170">161-180</option>
      <option value="190">181-200</option>
      <option value="210">201-220</option>
      <option value="230">221-240</option>
      <option value="250">241-260</option>
      <option value="270">261-280</option>
      <option value="290">281-300</option>
      <option value="310">301-320</option>
      <option value="330">321-340</option>
      <option value="350">341-360</option>
      <option value="370">361-380</option>
      <option value="390">381-400</option>
      <option value="410">401-420</option>
      <option value="430">421-440</option>
      <option value="450">441-460</option>
      <option value="470">461-480</option>
      <option value="490">481-500</option>
      <option value="510">501-520</option>
      <option value="530">521-540</option>
      <option value="550">541-560</option>
      <option value="570">561-580</option>
      <option value="590">581-600</option>
      <option value="610">601-620</option>
      <option value="630">621-640</option>
      <option value="650">641-660</option>
      <option value="670">661-680</option>
      <option value="690">681-700</option>
      <option value="710">701-720</option>
      <option value="730">721-740</option>
      <option value="750">741-760</option>
      <option value="770">761-780</option>
      <option value="790">781-800</option>
      <option value="810">801-820</option>
      <option value="830">821-840</option>
      <option value="850">841-860</option>
      <option value="870">861-880</option>
      <option value="890">881-900</option>
      <option value="910">901-920</option>
      <option value="930">921-940</option>
      <option value="950">941-960</option>
      <option value="970">961-980</option>
      <option value="990">981-1000</option>
    </select>
  </div>
)}

        
        {predictionType === 'over_runs' && (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">Select Innings</label>
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: match.matchType === 'test' ? 4 : 2 }, (_, i) => {
                  const inningNumber = i + 1;
                  const disableInning = match.scorecard?.innings?.length > inningNumber;
                  const ordinal = ['st', 'nd', 'rd', 'th'][i];

                  return (
                    <label
                      key={inningNumber}
                      className={`flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg p-3 cursor-pointer border-2 transition-colors ${
                        selectedInning === String(inningNumber) ? 
                        'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 
                        'border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
                      } ${disableInning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="inning"
                        className="sr-only"
                        value={inningNumber}
                        checked={selectedInning === String(inningNumber)}
                        onChange={(e) => setSelectedInning(e.target.value)}
                        disabled={disableInning}
                        
                      />
                      <div className={`w-4 h-4 mr-2 rounded-full border flex-shrink-0 flex items-center justify-center
                        ${selectedInning === String(inningNumber) ? 
                        'border-blue-600 bg-blue-600 dark:border-blue-400 dark:bg-blue-500' : 
                        'border-gray-400 dark:border-gray-500'
                      }`}>
                        {selectedInning === String(inningNumber) && 
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        }
                      </div>
                      <span className="text-sm font-medium truncate dark:text-white">
                        {inningNumber}{ordinal} Innings
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">
                Over Number ({match.matchType || 'T20'} Format)
              </label>
              <select
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 appearance-none"
                value={overNumber}
                onChange={(e) => setOverNumber((e.target.value))}
                required
              >
                <option value="">Select Over</option>
                {(() => {
                  const innings = match.scorecard?.innings || [];
                  const lastInning = innings[innings.length - 1];

                  let nextOver = 0;

                  if (lastInning && lastInning.overs !== undefined) {
                    const overParts = lastInning.overs.toString().split('.');
                    const fullOvers = parseInt(overParts[0]);
                    const balls = overParts[1] ? parseInt(overParts[1]) : 0;
                    nextOver = balls > 0 ? fullOvers + 1 : fullOvers;
                  }

                  // Skip ahead by 3 overs
                  const startOver = nextOver + 3;
                  const maxOvers = 1000; // adjust by format if needed

                  const options = [];
                  for (let i = startOver; i <= maxOvers; i++) {
                    options.push(
                      <option key={i} value={i}>
                        Over {i}
                      </option>
                    );
                  }

                  return options;
                })()}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">Runs in Over</label>
              <select
  className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
  value={overRuns}
  onChange={(e) => setOverRuns(e.target.value)}
  required
>
  <option value="2">0-4</option>
  <option value="7">5-9</option>
  <option value="12">10-14</option>
  <option value="17">15-19</option>
  <option value="22">20-24</option>
  <option value="27">25-29</option>
  <option value="32">30-34</option>
  <option value="37">35-39</option>
</select>

            </div>
          </div>
        )}

        {predictionType === 'over_wickets' && (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">Select Innings</label>
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: match.matchType === 'test' ? 4 : 2 }, (_, i) => {
                  const inningNumber = i + 1;
                  const disableInning = match.scorecard?.innings?.length > inningNumber;
                  const ordinal = ['st', 'nd', 'rd', 'th'][i];

                  return (
                    <label
                      key={inningNumber}
                      className={`flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg p-3 cursor-pointer border-2 transition-colors ${
                        selectedInning === String(inningNumber) ? 
                        'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 
                        'border-transparent hover:bg-gray-100 dark:hover:bg-gray-600'
                      } ${disableInning ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="inning"
                        className="sr-only"
                        value={inningNumber}
                        checked={selectedInning === String(inningNumber)}
                        onChange={(e) => setSelectedInning(e.target.value)}
                        disabled={disableInning}
                      />
                      <div className={`w-4 h-4 mr-2 rounded-full border flex-shrink-0 flex items-center justify-center
                        ${selectedInning === String(inningNumber) ? 
                        'border-blue-600 bg-blue-600 dark:border-blue-400 dark:bg-blue-500' : 
                        'border-gray-400 dark:border-gray-500'
                      }`}>
                        {selectedInning === String(inningNumber) && 
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        }
                      </div>
                      <span className="text-sm font-medium truncate dark:text-white">
                        {inningNumber}{ordinal} Innings
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">
                Over Number ({match.matchType || 'T20'} Format)
              </label>
              <select
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 appearance-none"
                value={overNumber}
                onChange={(e) => setOverNumber((e.target.value))}
                required
              >
                <option value="">Select Over</option>
                {(() => {
                  const innings = match.scorecard?.innings || [];
                  const lastInning = innings[innings.length - 1];

                  let nextOver = 0;

                  if (lastInning && lastInning.overs !== undefined) {
                    const overParts = lastInning.overs.toString().split('.');
                    const fullOvers = parseInt(overParts[0]);
                    const balls = overParts[1] ? parseInt(overParts[1]) : 0;
                    nextOver = balls > 0 ? fullOvers + 1 : fullOvers;
                  }

                  // Skip ahead by 3 overs
                  const startOver = nextOver + 3;
                  const maxOvers = 1000; // adjust by format if needed

                  const options = [];
                  for (let i = startOver; i <= maxOvers; i++) {
                    options.push(
                      <option key={i} value={i}>
                        Over {i}
                      </option>
                    );
                  }

                  return options;
                })()}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm font-medium">Wickets in Over</label>
              <input 
                type="number" 
                min="0"
                max="6"
                className="w-full p-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                value={overWickets}
                onChange={(e) => setOverWickets(e.target.value)}
                placeholder="Enter predicted wickets (0-6)"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Maximum 6 wickets possible per over</p>
            </div>
          </div>
        )}
        
        <div className="pt-4">
          <button 
            type="submit"
            className="w-full bg-blue-600 text-white py-3.5 rounded-xl hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed font-medium text-sm flex justify-center items-center"
            disabled={isSubmitting || (wallets.walletA || 0) < parseFloat(betAmount) || !predictionType}
          >
            {isSubmitting ? 
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Processing...
              </> : 
              !predictionType ? 'Select a Prediction Type' : `Place Bet (${betAmount} coins)`
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default PredictionForm;